import type { User } from 'firebase/auth';
import { ADMIN_DOMAIN } from './firebaseAuth';

// Resultado discriminado da validação de identidade do super-admin.
// Cada variante carrega o motivo específico para que a tela de "Acesso
// restrito" possa mostrar mensagem precisa em vez de erro genérico.
export type AdminAuthStatus =
  | { kind: 'LOADING' }
  | { kind: 'SIGNED_OUT' }
  | { kind: 'AUTHORIZED'; user: User }
  | { kind: 'WRONG_DOMAIN'; user: User; email: string }
  | { kind: 'EMAIL_NOT_VERIFIED'; user: User; email: string }
  | { kind: 'MISSING_CLAIM'; user: User; email: string };

/** Claims do ID token que o admin lê; `superAdmin` é concedido pelo servidor (`setSuperAdmin`). */
export type IdTokenClaims = {
  superAdmin?: boolean;
  email?: string;
  email_verified?: boolean;
};

/**
 * Aplica as três camadas de validação na ordem mais útil para diagnóstico:
 * 1) email pertence a `@etiquetachef.com`,
 * 2) email_verified pelo Google Workspace,
 * 3) custom claim `superAdmin === true`.
 *
 * Falha em qualquer camada produz uma variante específica que a UI usa
 * para explicar exatamente o que está errado.
 *
 * Origem: sem issue — commit 88be39195, 2026-04-26 (Sprint 2, Google SSO
 * restrito + guard de superAdmin).
 *
 * Contrato: função pura; não lança. E-mail vem do `User` e, na falta, do
 * claim. Verificado vale se o `User` OU o claim disser que sim.
 *
 * Usado por: `auth/AuthProvider.tsx` (a cada troca de token); a variante de
 * recusa é renderizada por `routes/RestrictedAccess.tsx`. Testes em
 * `auth/authStatus.test.ts`.
 *
 * Arquitetura: veredito de apresentação. A autorização real é o mesmo claim
 * conferido nas `firestore.rules` e nas callables.
 *
 * Dicas: a ordem das camadas é a do diagnóstico mais útil — conta de fora do
 * domínio aparece como WRONG_DOMAIN mesmo sem claim; mudar a ordem muda a
 * mensagem que o usuário vê.
 */
export function evaluateAdmin(user: User, claims: IdTokenClaims): AdminAuthStatus {
  const email = user.email ?? claims.email ?? '';
  if (!email.toLowerCase().endsWith(`@${ADMIN_DOMAIN}`)) {
    return { kind: 'WRONG_DOMAIN', user, email };
  }
  const verified = user.emailVerified || claims.email_verified === true;
  if (!verified) {
    return { kind: 'EMAIL_NOT_VERIFIED', user, email };
  }
  if (claims.superAdmin !== true) {
    return { kind: 'MISSING_CLAIM', user, email };
  }
  return { kind: 'AUTHORIZED', user };
}

/**
 * Marcas da sessão da página que o `AuthProvider` carrega entre emissões do
 * `onIdTokenChanged`: de quem o token já foi renovado à força e quem já foi
 * anunciado como logado.
 */
export type TokenSessionMarks = {
  /** uid cujo token já passou por `getIdTokenResult(true)` nesta página. */
  forcedUid: string | null;
  /**
   * uid já anunciado como AUTHORIZED nesta página (`identify` emitido; o
   * `login` só sai se houve sign-in por popup, `consumeSignInIntent`, #1037).
   */
  authorizedUid: string | null;
};

/** Estado inicial das marcas: ninguém renovado, ninguém anunciado. */
export const EMPTY_TOKEN_SESSION_MARKS: TokenSessionMarks = {
  forcedUid: null,
  authorizedUid: null,
};

/**
 * Decide se a emissão de token do `uid` deve forçar o refresh.
 *
 * Origem: #963 — 2026-10-13 17:45 +0200.
 *
 * Por quê: o refresh forçado existe para que um claim `superAdmin` recém-
 * concedido apareça sem esperar até 1 h. Mas `getIdTokenResult(true)` emite um
 * token novo, e isso dispara o próprio `onIdTokenChanged` de novo. Forçar em
 * toda emissão era um loop: 78.349 `login` em 7 dias de uma pessoa só, com a
 * callable `verifyAdminLogin` em rajada (#963). Forçar só na primeira emissão
 * de cada uid mantém a propagação do claim e corta o loop.
 *
 * Usado por: `AuthProvider` (`auth/AuthProvider.tsx`); `authStatus.test.ts`.
 *
 * Dicas: troca de conta (uid diferente) força de novo — de propósito, o claim
 * do novo usuário pode ser recente.
 */
export function shouldForceRefresh(marks: TokenSessionMarks, uid: string): boolean {
  return marks.forcedUid !== uid;
}

/**
 * Decide se a emissão AUTHORIZED do `uid` é uma entrada nova (emite `login` e
 * `identify`) ou só uma renovação do token de quem já estava dentro.
 *
 * Origem: #963 — 2026-10-13 17:45 +0200.
 * Usado por: `AuthProvider`; `authStatus.test.ts`.
 */
export function isNewAuthorizedSession(marks: TokenSessionMarks, uid: string): boolean {
  return marks.authorizedUid !== uid;
}
